import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Button, Input, Card } from '../../components/ui';
import type { Band, VibeTag, MusicTag, CrowdPreference } from '../../types/database';
import styles from './BandEditor.module.css';

interface Props {
  band: Band;
}

const VIBE_OPTIONS: { value: VibeTag; label: string }[] = [
  { value: 'bacchanal', label: 'Bacchanal' },
  { value: 'pretty', label: 'Pretty Mas' },
  { value: 'music', label: 'Music-focused' },
  { value: 'social', label: 'Social' },
  { value: 'cultural', label: 'Cultural' },
];

const MUSIC_OPTIONS: { value: MusicTag; label: string }[] = [
  { value: 'soca', label: 'Soca' },
  { value: 'dancehall', label: 'Dancehall' },
  { value: 'afrobeats', label: 'Afrobeats' },
  { value: 'bouyon', label: 'Bouyon' },
];

const CROWD_OPTIONS: { value: CrowdPreference; label: string }[] = [
  { value: 'young', label: 'Young (18-30)' },
  { value: 'mixed', label: 'Mixed ages' },
  { value: 'mature', label: 'Mature (35+)' },
];

export function BandEditor({ band }: Props) {
  const [form, setForm] = useState({
    name: band.name,
    description: band.description || '',
    website_url: band.website_url || '',
    instagram_url: band.instagram_url || '',
    price_min: band.price_min?.toString() || '',
    price_max: band.price_max?.toString() || '',
    vibe_tags: band.vibe_tags || [],
    music_tags: band.music_tags || [],
    crowd_type: band.crowd_type || 'mixed',
    section_count: band.section_count?.toString() || '',
    inclusives: band.inclusives?.join(', ') || '',
    route_description: band.route_description || '',
  });

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleChange = (field: string, value: string | string[]) => {
    setForm({ ...form, [field]: value });
    setSaved(false);
  };

  const toggleArrayValue = (field: 'vibe_tags' | 'music_tags', value: string) => {
    const current = form[field] as string[];
    if (current.includes(value)) {
      handleChange(field, current.filter((v) => v !== value));
    } else {
      handleChange(field, [...current, value]);
    }
  };

  const handleSave = async () => {
    setSaving(true);

    const { error } = await supabase
      .from('bands')
      .update({
        name: form.name,
        description: form.description || null,
        website_url: form.website_url || null,
        instagram_url: form.instagram_url || null,
        price_min: form.price_min ? parseInt(form.price_min) : null,
        price_max: form.price_max ? parseInt(form.price_max) : null,
        vibe_tags: form.vibe_tags,
        music_tags: form.music_tags,
        crowd_type: form.crowd_type as CrowdPreference,
        section_count: form.section_count ? parseInt(form.section_count) : null,
        inclusives: form.inclusives
          ? form.inclusives.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
        route_description: form.route_description || null,
      })
      .eq('id', band.id);

    setSaving(false);

    if (!error) {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
  };

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1>Edit Band Profile</h1>
        <div className={styles.headerActions}>
          {saved && <span className={styles.savedBadge}>Saved!</span>}
          <Button onClick={handleSave} loading={saving}>
            Save Changes
          </Button>
        </div>
      </header>

      <div className={styles.form}>
        {/* Basic Info */}
        <Card className={styles.section}>
          <h3>Basic Information</h3>
          <div className={styles.fields}>
            <Input
              label="Band Name"
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
            />
            <div className={styles.textareaField}>
              <label>Description</label>
              <textarea
                value={form.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Tell masqueraders about your band..."
                rows={4}
              />
            </div>
          </div>
        </Card>

        {/* Links */}
        <Card className={styles.section}>
          <h3>Links</h3>
          <div className={styles.fields}>
            <Input
              label="Website URL"
              type="url"
              value={form.website_url}
              onChange={(e) => handleChange('website_url', e.target.value)}
              placeholder="https://yourband.com"
            />
            <Input
              label="Instagram URL"
              type="url"
              value={form.instagram_url}
              onChange={(e) => handleChange('instagram_url', e.target.value)}
              placeholder="https://instagram.com/yourband"
            />
          </div>
        </Card>

        {/* Pricing */}
        <Card className={styles.section}>
          <h3>Pricing (USD)</h3>
          <div className={styles.row}>
            <Input
              label="Minimum Price"
              type="number"
              value={form.price_min}
              onChange={(e) => handleChange('price_min', e.target.value)}
              placeholder="e.g., 500"
            />
            <Input
              label="Maximum Price"
              type="number"
              value={form.price_max}
              onChange={(e) => handleChange('price_max', e.target.value)}
              placeholder="e.g., 1500"
            />
          </div>
          <Input
            label="Number of Sections"
            type="number"
            value={form.section_count}
            onChange={(e) => handleChange('section_count', e.target.value)}
            placeholder="e.g., 10"
          />
        </Card>

        {/* Vibes */}
        <Card className={styles.section}>
          <h3>Vibe Tags</h3>
          <p className={styles.hint}>Select the vibes that best describe your band</p>
          <div className={styles.tags}>
            {VIBE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`${styles.tag} ${form.vibe_tags.includes(option.value) ? styles.selected : ''}`}
                onClick={() => toggleArrayValue('vibe_tags', option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </Card>

        {/* Music */}
        <Card className={styles.section}>
          <h3>Music Tags</h3>
          <p className={styles.hint}>Select the music styles on your trucks</p>
          <div className={styles.tags}>
            {MUSIC_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`${styles.tag} ${form.music_tags.includes(option.value) ? styles.selected : ''}`}
                onClick={() => toggleArrayValue('music_tags', option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </Card>

        {/* Crowd */}
        <Card className={styles.section}>
          <h3>Target Crowd</h3>
          <div className={styles.tags}>
            {CROWD_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`${styles.tag} ${form.crowd_type === option.value ? styles.selected : ''}`}
                onClick={() => handleChange('crowd_type', option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </Card>

        {/* Inclusives */}
        <Card className={styles.section}>
          <h3>Inclusives</h3>
          <p className={styles.hint}>Comma-separated list (e.g., Drinks, Food, Sunscreen)</p>
          <Input
            value={form.inclusives}
            onChange={(e) => handleChange('inclusives', e.target.value)}
            placeholder="Drinks, Food, Sunscreen, Security, Bathrooms"
          />
        </Card>

        {/* Route */}
        <Card className={styles.section}>
          <h3>Route Description</h3>
          <div className={styles.textareaField}>
            <textarea
              value={form.route_description}
              onChange={(e) => handleChange('route_description', e.target.value)}
              placeholder="Describe your route and any special stops..."
              rows={3}
            />
          </div>
        </Card>
      </div>
    </div>
  );
}
